import type {
  Material,
  MaterialComponent,
  MaterialRouting,
  MaterialWithSoftTag,
  SelectedItemType,
  AssetSoftTag,
  SoftTag,
  TagMappingRequest,
  TimeSeriesSoftTag,
} from './match-maker.types';

export const getItemNumber = (
  selectedItem?: Material | MaterialComponent | MaterialRouting
) => {
  if (!selectedItem) {
    return '-';
  }

  if ('material_code' in selectedItem) {
    return selectedItem.material_code;
  }

  if ('component' in selectedItem) {
    return selectedItem.component;
  }

  if ('workcenter' in selectedItem) {
    return selectedItem.workcenter;
  }

  return '-';
};

export const getItemTypeLabel = (selectedType?: SelectedItemType) => {
  if (selectedType === 'material') {
    return 'Output material';
  }

  if (selectedType === 'component') {
    return 'Component';
  }

  if (selectedType === 'routing') {
    return 'Utility';
  }

  if (selectedType === 'tag') {
    return 'Tag';
  }

  return '-';
};

export const getItemTypeFromSelectedItem = (
  selectedItem?: Material | MaterialComponent | MaterialRouting
) => {
  if (!selectedItem) {
    return '-';
  }

  if ('material_code' in selectedItem) {
    return 'Output material';
  }

  if ('workcenter' in selectedItem) {
    return 'Utility';
  }

  if ('asset_id' in selectedItem) {
    return 'Tag';
  }

  if ('type' in selectedItem) {
    return displayMaterialType(selectedItem.type || '');
  }

  return '-';
};

export const getItemDescription = (
  selectedItem?: Material | MaterialComponent | MaterialRouting
) => {
  if (!selectedItem) {
    return '-';
  }

  if ('material_code' in selectedItem || 'component' in selectedItem) {
    return selectedItem.description;
  }

  if ('workcenter' in selectedItem) {
    return selectedItem.object_id;
  }
  return '-';
};

export const getSelectedTagLabel = (tag: MaterialWithSoftTag) => {
  return tag.timeseries_id || tag.id;
};

export const getSelectedItemFamily = (
  selectedItem?: Material | MaterialComponent | MaterialRouting
) => {
  if (!selectedItem) {
    return '-';
  }

  if ('material_code' in selectedItem) {
    return 'Production';
  }
  if ('component' in selectedItem || 'workcenter' in selectedItem) {
    return 'Consumption';
  }

  return '-';
};

export const mapTimeSeriesToSoftTag = (item: TimeSeriesSoftTag): SoftTag => {
  return {
    id: '', // This will be generated when the tag is created in the system
    timeseries_id: item.id,
    tag_name: item.tag_name,
    site_id: item.site_id,
    area: item.area,
    asset_name: item.name,
    tag_description: item.description,
    production_unit: item.production_unit,
    asset_id: item.asset_id,
    type: item.type,
    unit: item.unit,
    data_type: item.data_type,
    created_at: item.created_at,
    updated_at: item.updated_at,
    priority: 0, // Default priority, will be updated later if selected
  };
};

export const mapAssetToSoftTag = (item: AssetSoftTag): SoftTag => {
  return {
    id: '',
    timeseries_id: item.id,
    tag_name: item.tag_name,
    site_id: item.site,
    area: item.area,
    asset_name: item.name,
    tag_description: item.description || item.name,
    production_unit: item.production_unit,
    asset_id: item.id,
    type: item.name,
    data_type: item.data_type,
    unit: item.unit,
    priority: 0,
    created_at: item.created_at,
    updated_at: item.updated_at,
  };
};

export const mapMaterialTagToSoftTag = (tag: MaterialWithSoftTag): SoftTag => {
  return {
    id: tag.id,
    tag_name: tag.tag_name,
    site_id: tag.site_id,
    area: tag.area,
    asset_name: tag.asset_name,
    tag_description: tag.tag_description,
    production_unit: tag.production_unit,
    asset_id: tag.asset_id,
    type: tag.type,
    data_type: tag.data_type,
    unit: tag.unit,
    priority: tag.priority,
    created_at: tag.tag_created_at,
    timeseries_id: tag.timeseries_id,
  };
};

export const mapSoftTagToTagMappingRequest = (
  item: SoftTag,
  ids: {
    materialId: string | null;
    componentId: string | null;
    routingId: string | null;
  }
): TagMappingRequest => ({
  material_id: ids.materialId,
  component_id: ids.componentId,
  routing_id: ids.routingId,
  asset_id: item.asset_id,
  timeseries_id: item.timeseries_id ?? '',
  asset_name: item.asset_name,
  tag_description: item.tag_description,
  tag_name: item.tag_name,
  site_id: item.site_id,
  area: item.area,
  production_unit: item.production_unit,
  type: item.type ?? '',
  data_type: item.data_type ?? '',
  unit: item.unit ?? '',
  tag_created_at: item.created_at,
  priority: item.priority,
});

export const displayMaterialType = (type: string) => {
  const normalized = type.trim();
  if (!normalized) {
    return '-';
  }
  switch (normalized.toUpperCase()) {
    case 'INPUT':
      return 'Input material';
    case 'OUTPUT':
      return 'Output material';
    case 'BY_PRODUCT':
      return 'Byproduct';
    case 'UTILITY':
      return 'Utility';
    default: {
      const sentence = normalized.toLowerCase().replace(/_/g, ' ');
      return sentence.charAt(0).toUpperCase() + sentence.slice(1);
    }
  }
};

export const getItemIdentifier = (
  selectedType: SelectedItemType,
  selectedItem?: Material | MaterialComponent | MaterialRouting
) => {
  if (!selectedType || !selectedItem) {
    return null;
  }
  let identifier: string | null = null;
  switch (selectedType) {
    case 'material': {
      if ('material_code' in selectedItem) {
        identifier = selectedItem.id;
      }
      break;
    }
    case 'component': {
      if ('component' in selectedItem) {
        identifier = selectedItem.id;
      }
      break;
    }
    case 'routing':
      {
        if ('workcenter' in selectedItem) {
          identifier = selectedItem.id;
        }
      }
      break;
    default:
      identifier = null;
  }
  return identifier;
};
